import { supabase } from "@/integrations/supabase/client";
import {
  ACCESSORIES,
  type AccessoryDefinition,
  type CabinetType,
  ALL_CABINET_TYPES,
} from "@/data/cabinetDefaults";

/**
 * Built-in accessory snapshots, captured at module load BEFORE any DB
 * overrides have been applied. Used to (a) detect which DB rows are
 * pure overrides vs. brand-new custom accessories, and (b) restore a
 * built-in accessory if its override row is deleted.
 */
const BUILTIN_IDS = new Set(ACCESSORIES.map((a) => a.id));
const BUILTIN_SNAPSHOT: Record<string, AccessoryDefinition> = Object.fromEntries(
  ACCESSORIES.map((a) => [a.id, structuredClone(a)]),
);

export type AccessoryOverrideRow = {
  id: string;
  label: string | null;
  cabinet_types: string[] | null;
  min_width: number | null;
  max_width: number | null;
  requires_standard_depth: boolean | null;
  requires_door_drawer: boolean | null;
  shallow_drawer_insert: boolean | null;
  deep_drawer_insert: boolean | null;
  requires_side_selection: boolean | null;
  is_custom_request: boolean | null;
  conflict_group: string | null;
  description: string | null;
  price: number | null;
  allow_multiple?: boolean | null;
  max_quantity?: number | null;
  sort_order: number;
  hidden: boolean;
  is_custom: boolean;
};

export function isBuiltinAccessoryId(id: string) {
  return BUILTIN_IDS.has(id);
}

/** Merge a DB row over the built-in snapshot (or build from scratch for custom). */
function rowToAccessory(row: AccessoryOverrideRow): AccessoryDefinition | null {
  const base: AccessoryDefinition | undefined = BUILTIN_SNAPSHOT[row.id]
    ? structuredClone(BUILTIN_SNAPSHOT[row.id])
    : undefined;

  if (!base && !row.is_custom) {
    // Orphan override for a built-in that no longer exists in code. Ignore.
    return null;
  }

  const merged: AccessoryDefinition = base ?? {
    id: row.id,
    label: row.label ?? row.id,
    cabinetTypes: (row.cabinet_types ?? []) as CabinetType[],
    price: row.price ?? 0,
  };

  if (row.label != null) merged.label = row.label;
  if (row.cabinet_types != null)
    merged.cabinetTypes = row.cabinet_types.filter((t): t is CabinetType =>
      (ALL_CABINET_TYPES as string[]).includes(t),
    );
  if (row.min_width != null) merged.minWidth = row.min_width;
  if (row.max_width != null) merged.maxWidth = row.max_width;
  if (row.requires_standard_depth != null)
    merged.requiresStandardDepth = row.requires_standard_depth;
  if (row.requires_door_drawer != null)
    merged.requiresDoorDrawer = row.requires_door_drawer;
  if (row.shallow_drawer_insert != null)
    merged.shallowDrawerInsert = row.shallow_drawer_insert;
  if (row.deep_drawer_insert != null)
    merged.deepDrawerInsert = row.deep_drawer_insert;
  if (row.requires_side_selection != null)
    merged.requiresSideSelection = row.requires_side_selection;
  if (row.is_custom_request != null) merged.isCustomRequest = row.is_custom_request;
  if (row.conflict_group != null) merged.conflictGroup = row.conflict_group || undefined;
  if (row.description != null) merged.description = row.description;
  if (row.price != null) merged.price = row.price;
  if (row.allow_multiple != null) merged.allowMultiple = row.allow_multiple;
  if (row.max_quantity != null) merged.maxQuantity = row.max_quantity;

  return merged;
}

let loadPromise: Promise<void> | null = null;

/**
 * Loads accessory overrides from Supabase and mutates the shared
 * ACCESSORIES array in place so all consumers see the latest catalog
 * without needing to re-import.
 */
export async function loadAccessoryOverrides(force = false): Promise<void> {
  if (loadPromise && !force) return loadPromise;
  loadPromise = (async () => {
    const { data, error } = await supabase
      .from("accessory_overrides")
      .select("*");
    if (error) {
      console.warn("[accessoriesCatalog] failed to load:", error.message);
      return;
    }
    const rows = (data ?? []) as AccessoryOverrideRow[];

    // Start from a fresh copy of all built-ins, then apply overrides.
    const next: AccessoryDefinition[] = Object.values(BUILTIN_SNAPSHOT).map((a) =>
      structuredClone(a),
    );
    const byId = new Map(next.map((a) => [a.id, a]));

    for (const row of rows) {
      if (row.hidden && BUILTIN_IDS.has(row.id)) {
        byId.delete(row.id);
        continue;
      }
      const merged = rowToAccessory(row);
      if (!merged) continue;
      byId.set(row.id, merged);
    }

    // Apply DB sort_order (built-ins default to their snapshot order).
    const sortOrderById = new Map<string, number>();
    rows.forEach((r) => sortOrderById.set(r.id, r.sort_order ?? 0));
    const originalIndex = new Map<string, number>();
    Object.keys(BUILTIN_SNAPSHOT).forEach((id, i) => originalIndex.set(id, i));
    const sorted = Array.from(byId.values()).sort((a, b) => {
      const sa = sortOrderById.get(a.id) ?? 0;
      const sb = sortOrderById.get(b.id) ?? 0;
      if (sa !== sb) return sa - sb;
      const oa = originalIndex.get(a.id) ?? Number.MAX_SAFE_INTEGER;
      const ob = originalIndex.get(b.id) ?? Number.MAX_SAFE_INTEGER;
      return oa - ob;
    });

    // Mutate the shared array in place.
    ACCESSORIES.length = 0;
    ACCESSORIES.push(...sorted);
  })();
  return loadPromise;
}

export async function fetchAccessoryOverrides(): Promise<AccessoryOverrideRow[]> {
  const { data, error } = await supabase
    .from("accessory_overrides")
    .select("*");
  if (error) throw error;
  return (data ?? []) as AccessoryOverrideRow[];
}

export type AccessoryUpsertInput = Omit<AccessoryOverrideRow, "sort_order"> & {
  sort_order?: number;
};

export async function upsertAccessoryOverride(row: AccessoryUpsertInput): Promise<void> {
  const { error } = await supabase
    .from("accessory_overrides")
    .upsert(
      {
        id: row.id,
        label: row.label,
        cabinet_types: row.cabinet_types,
        min_width: row.min_width,
        max_width: row.max_width,
        requires_standard_depth: row.requires_standard_depth,
        requires_door_drawer: row.requires_door_drawer,
        shallow_drawer_insert: row.shallow_drawer_insert,
        deep_drawer_insert: row.deep_drawer_insert,
        requires_side_selection: row.requires_side_selection,
        is_custom_request: row.is_custom_request,
        conflict_group: row.conflict_group,
        description: row.description,
        price: row.price,
        allow_multiple: row.allow_multiple ?? null,
        max_quantity: row.max_quantity ?? null,
        sort_order: row.sort_order ?? 0,
        hidden: row.hidden,
        is_custom: row.is_custom,
      },
      { onConflict: "id" },
    );
  if (error) throw error;
}

export async function deleteAccessoryOverride(id: string): Promise<void> {
  const { error } = await supabase
    .from("accessory_overrides")
    .delete()
    .eq("id", id);
  if (error) throw error;
}
